import { addMonths } from 'date-fns/addMonths';
import { addYears } from 'date-fns/addYears';
import { isSameDay } from 'date-fns/isSameDay';
import { isToday } from 'date-fns/isToday';
import { set } from 'date-fns/set';

import { getZonedDate, ZonedDate, format } from '@eika-infrastruktur/date';

import { format as translationFormat } from '../../../../hooks/useTranslation';

const DATE_FORMAT = 'dd.MM.yyyy';

type DateSetParams = {
    year?: number;
    month?: number;
    date?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
};

const defaultTime = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 1,
};

const getAndSetDate = (dateToSet: Date, params: DateSetParams) =>
    getZonedDate(set(dateToSet, { ...defaultTime, ...params }));

export const getDateFromTwelveMonthsAgo = (date?: ZonedDate) => {
    const targetDate = date ?? getZonedDate();
    return getAndSetDate(targetDate, { month: targetDate.getMonth() - 12 }) ?? getZonedDate();
};

export const getDateTwelveMonthsInTheFuture = (date?: ZonedDate) => {
    const today = getZonedDate();
    const targetDate = date ?? today;
    const dateTwoMonthsAway = getAndSetDate(targetDate, { month: targetDate.getMonth() + 12 }) ?? today;
    if (dateTwoMonthsAway >= today) {
        return today;
    }
    return dateTwoMonthsAway;
};

export const isOneYearFromToday = (fromDate: ZonedDate | undefined) =>
    fromDate && isSameDay(getZonedDate(), addYears(fromDate, 1));

export const isSixMonthsFromToday = (fromDate: ZonedDate | undefined) =>
    fromDate && isSameDay(getZonedDate(), addMonths(fromDate, 6));

export const isTwoMonthsFromToday = (fromDate: ZonedDate | undefined) =>
    fromDate && isSameDay(getZonedDate(), addMonths(fromDate, 2));

export const isOneMonthFromToday = (fromDate: ZonedDate | undefined) =>
    fromDate && isSameDay(getZonedDate(), addMonths(fromDate, 1));

export const formatDateRangeMessage = (
    actualFromDate: ZonedDate | undefined,
    actualToDate: ZonedDate | undefined,
    messages: { lastMonths: string; lastMonth: string },
) => {
    if (!actualFromDate && !actualToDate) {
        return translationFormat(messages.lastMonths, 12);
    }

    if (actualToDate && isToday(actualToDate) && isOneYearFromToday(actualFromDate))
        return translationFormat(messages.lastMonths, 12);
    if (actualToDate && isToday(actualToDate) && isSixMonthsFromToday(actualFromDate))
        return translationFormat(messages.lastMonths, 6);
    if (actualToDate && isToday(actualToDate) && isTwoMonthsFromToday(actualFromDate))
        return translationFormat(messages.lastMonths, 2);
    if (actualToDate && isToday(actualToDate) && isOneMonthFromToday(actualFromDate)) return messages.lastMonth;

    const toDate = actualToDate ?? getDateTwelveMonthsInTheFuture(actualFromDate);
    const fromDate = actualFromDate ?? getDateFromTwelveMonthsAgo(actualToDate);
    const formattedFromDate = format(fromDate, DATE_FORMAT);
    const formattedToDate = format(toDate, DATE_FORMAT);

    return `${formattedFromDate}-${formattedToDate}`;
};
